import { useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useGetCaseDelayBreakdownQuery from "../hooks/useGetCaseDelayBreakdownQuery";
import BarChart, {
  BAR_GRAPH_DIRECTION,
} from "../../../components/graphs/BarChart";
import { useTranslation } from "react-i18next";
import useGetAllOverviewMetricsQuery from "../hooks/useGetAllOverviewMetricsQuery";
import useGetCaseIntervalQuery from "../hooks/useGetCaseIntervalQuery";
import BarGraphLoader from "../../../components/skeletonLoaders/BarGraphLoader";
import { GET_CASE_INTERVAL_RESPONSE } from "../types/ResponseTypes";
import useGetOtTimeBreakdownQuery from "../hooks/useGetOtTimeBreakdownQuery";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useFilterValue from "../hooks/useFilterValue";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
import isNil from "../../../helpers/isNil";
import useGetAnalyticsProcedureQuery from "../hooks/useGetAnalyticsProcedureQuery";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import CustomTooltip from "../../../components/CustomTooltip";
import getOverviewMetricData from "../helpers/getOverviewMetricData";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import ChartContainer, {
  CHART_TYPE,
  RenderChartProps,
} from "../../../components/ChartContainer";
import useDashboardExport from "../hooks/useDashboardExport";
import ScatterPlot from "../../../components/graphs/ScatterPlot";
import ScatterPlotLoader from "../../../components/skeletonLoaders/ScatterPlotLoader";
import PieChart from "../../../components/graphs/PieChart";
import PieChartLoader from "../../../components/skeletonLoaders/PieChartLoader";
import useGetDelayedStartSeverityFrequencyQuery from "../hooks/useGetDelayedStartSeverityFrequencyQuery";
import useGetDelayedEndSeverityFrequencyQuery from "../hooks/useGetDelayedEndSeverityFrequencyQuery";
import { capitalizeFirstWord } from "../../../helpers/capitalize";
import useGetCasesMovedPerOtQuery from "../hooks/useGetCasesMovedPerOtQuery";

const overviewMatricsSortOrder = [
  "total cases",
  "fcots",
  "on time starts (all cases)",
  "on time ends (all cases)",
  "avg case delays",
  "ot utilization",
  "avg total case time",
  "room turnover time",
  "primary surgeon turnover time",
];

const caseIntervalBarDataArr = [
  {
    label: "Room Ready to Wheels In",
    key: "durationRoomReadyToWheelsIn",
  },
  {
    label: "Wheels In to Patient Ready",
    key: "durationWheelsInToPatientReady",
  },
  {
    label: "Patient Ready to Procedure Start",
    key: "durationPatientReadyToProcedureStart",
  },
  {
    label: "Procedure Start to End",
    key: "durationProcedureStartToProcedureEnd",
  },
  {
    label: "Procedure End to Wheels Out",
    key: "durationProcedureEndToWheelsOut",
  },
  {
    label: "WheelsOut to Room Clean Start",
    key: "durationWheelsOutToRoomClean",
  },
  {
    label: "Room Clean Start to Room Clean End",
    key: "durationRoomCleanStartToEnd",
  },
  {
    label: "Room Clean End to Room Ready",
    key: "roomSetupTime",
  },
];

interface MasterDataTableProps {
  exportOverviewScreenshot?: any;
}

// Extract data fetching hooks into a custom hook
const useOverviewData = (
  tenantId: string | undefined,
  hospitalId: string | undefined,
  queryString: string | undefined
) => {
  const { data: caseDelayBreakdown, isLoading: isGettingCaseBreakdown } =
    useGetCaseDelayBreakdownQuery(
      tenantId ?? "",
      hospitalId ?? "",
      queryString
    );
  const { data: allOverviewMetricsData = [] } = useGetAllOverviewMetricsQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString
  );
  const { data: caseIntervalData, isLoading: isGettingCaseInterval } =
    useGetCaseIntervalQuery(tenantId ?? "", hospitalId ?? "", queryString);
  const { data: otTimeData, isLoading: isGettingOtTIme } =
    useGetOtTimeBreakdownQuery(tenantId ?? "", hospitalId ?? "", queryString);
  const { data: caseVolumeSpeciality, isLoading: isGettingVolumeSpeciality } =
    useGetAnalyticsProcedureQuery(
      tenantId ?? "",
      hospitalId ?? "",
      queryString
    );
  const { data: casesMovedPerOt, isLoading: isGettingCasesMovedPerOt } =
    useGetCasesMovedPerOtQuery(tenantId ?? "", hospitalId ?? "", queryString);

  const sortedCaseDelayBreakdown = useMemo(() => {
    if (!caseDelayBreakdown) return [];
    return caseDelayBreakdown
      .map((item: any) => ({
        ...item,
        label: item.label.replace(/minute/g, "min"),
      }))
      .toSorted((a: any, b: any) => {
        if (a.label === "On time") return -1;
        if (b.label === "On time") return 1;
        const getStartMinutes = (label: string) => {
          const regex = /^(\d+)[+-]/;
          const match = regex.exec(label);
          return match ? parseInt(match[1], 10) : Infinity;
        };
        return getStartMinutes(a.label) - getStartMinutes(b.label);
      });
  }, [caseDelayBreakdown]);

  const sortedOverviewMetricsData = useMemo(() => {
    return allOverviewMetricsData.toSorted((a, b) => {
      const indexA = overviewMatricsSortOrder.indexOf(
        a.label.toLowerCase().trim()
      );
      const indexB = overviewMatricsSortOrder.indexOf(
        b.label.toLowerCase().trim()
      );

      // If both items are in the sort order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one item is in the sort order, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither item is in the sort order, maintain original order
      return 0;
    });
  }, [allOverviewMetricsData]);

  return {
    caseDelayBreakdown: sortedCaseDelayBreakdown,
    isGettingCaseBreakdown,
    allOverviewMetricsData: sortedOverviewMetricsData,
    caseIntervalData,
    isGettingCaseInterval,
    otTimeData,
    isGettingOtTIme,
    caseVolumeSpeciality,
    isGettingVolumeSpeciality,
    casesMovedPerOt,
    isGettingCasesMovedPerOt,
  };
};

const Overview: React.FC<MasterDataTableProps> = ({
  exportOverviewScreenshot,
}) => {
  const { t } = useTranslation();
  const currentHospital = useSelectedHospitalValue();
  const { tenantId: authTenantId, hospitalId: authHospitalId } = useAuthValue();
  const tenantId = currentHospital?.tenantId ?? authTenantId;
  const hospitalId = currentHospital?.uuid ?? authHospitalId;
  const screenshotRef = useRef(null);
  const [, takeScreenshot] = useScreenshot();
  const [isExporting, setIsExporting] = useState(false);
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters(filters);

  const {
    caseDelayBreakdown,
    isGettingCaseBreakdown,
    allOverviewMetricsData,
    caseIntervalData,
    isGettingCaseInterval,
    otTimeData,
    isGettingOtTIme,
    caseVolumeSpeciality,
    isGettingVolumeSpeciality,
    casesMovedPerOt,
    isGettingCasesMovedPerOt,
  } = useOverviewData(tenantId, hospitalId, queryString);

  // Fetch delayed start severity frequency data
  const {
    data: delayedStartSeverityFrequencyData,
    isLoading: isLoadingDelayedStartSeverityFrequency,
  } = useGetDelayedStartSeverityFrequencyQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString ?? ""
  );

  // Fetch delayed end severity frequency data
  const {
    data: delayedEndSeverityFrequencyData,
    isLoading: isLoadingDelayedEndSeverityFrequency,
  } = useGetDelayedEndSeverityFrequencyQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString ?? ""
  );

  const caseIntervalBarData = useMemo(() => {
    if (!caseIntervalData) return [];
    return caseIntervalBarDataArr.map((item) => ({
      label: item.label,
      value:
        caseIntervalData[item.key as keyof GET_CASE_INTERVAL_RESPONSE] ?? 0,
    }));
  }, [caseIntervalData]);

  const transformedCaseVolumeSpeciality = useMemo(() => {
    if (!caseVolumeSpeciality) return [];
    return caseVolumeSpeciality
      .slice(0, 6) // Limit to top 6 records
      .map((item: any) => ({
        label: item.label,
        value: item.count,
      }));
  }, [caseVolumeSpeciality]);

  const casesMovedPerOtData = useMemo(() => {
    if (!casesMovedPerOt?.otMovements) return [];

    return casesMovedPerOt.otMovements.map((ot: any) => ({
      label: ot.otName,
      value: ot.totalMovements,
    }));
  }, [casesMovedPerOt]);

  useDashboardExport({
    exportRef: exportOverviewScreenshot,
    screenshotRef,
    setIsExporting,
    takeScreenshot,
    filename: "orva-dashboard-overview.pdf",
  });

  const getAcronym = (label: string) => {
    if (!label) return "";
    return label
      .split(/[^A-Za-z0-9]+/)
      .map(word => word[0])
      .join("")
      .toUpperCase();
  };

  // Helper to map API data to scatterplot format
  const mapScatterData = (apiData: any) => {
    if (!Array.isArray(apiData)) return [];
    return apiData.map((item: any) => ({
      x: item.frequency,
      y: item.severity,
      label: getAcronym(item.reasonName),
      frequency: item.frequency,
      severity: item.severity,
      reasonName: capitalizeFirstWord(item.reasonName),
      color: item?.color,
      category: item?.category,
    }));
  };

  // Rendering functions
  const renderMetricCard = (item: any) => {
    const { label, value } = item;
    const roundedValue = Math.round(value);

    const { tooltip, conversionKey, title, subTitle } = getOverviewMetricData(
      label.toLowerCase().trim()
    );

    return (
      <Stack
        sx={{ cursor: "default" }}
        flex={1}
        gap={1}
        p={3}
        borderRadius={2}
        justifyContent="center"
        bgcolor="custom.background.primary"
      >
        <CustomTooltip key={label} title={t(tooltip)}>
          <Typography fontSize={18} color="custom.foreground.secondary">
            {t(title)}
          </Typography>
        </CustomTooltip>
        {subTitle && (
          <Typography
            fontWeight={500}
            fontSize={10}
            lineHeight={1}
            marginTop={-1}
            color="custom.foreground.secondary"
          >
            {t(subTitle)}
          </Typography>
        )}
        <Typography variant="h5">
          {!isNil(value)
            ? t(conversionKey, {
              val: roundedValue,
            })
            : "N/A"}
        </Typography>
      </Stack>
    );
  };

  const renderChart = ({
    data,
    title,
    tooltipTitle,
    isLoading,
    props,
    chartType,
  }: RenderChartProps) => {
    if (chartType === CHART_TYPE.SCATTER) {
      if (isLoading) {
        return <ScatterPlotLoader {...props} />;
      }

      const scatterData = mapScatterData(data || []);

      return (
        <ScatterPlot
          key={hospitalId}
          data={scatterData}
          title={t(title)}
          tooltipTitle={t(tooltipTitle)}
          xLabel="Frequency (# of cases)"
          yLabel="Severity (Avg mins)"
          {...props}
        />
      );
    }

    if (chartType === CHART_TYPE.PIE) {
      if (isLoading) {
        return <PieChartLoader />;
      }

      const valueKey = props?.valueKey ?? "value";
      const raw = Array.isArray(data) ? data : [];
      const total = raw.reduce(
        (sum: number, item: any) => sum + (Number(item?.[valueKey]) || 0),
        0
      );

      if (total <= 0) {
        return (
          <PieChart
            key={hospitalId}
            data={[]}
            title={t(title)}
            tooltipTitle={t(tooltipTitle)}
            labelSize={props?.categoryFontSize ?? 13}
            showLegend={props?.showLegend ?? true}
          />
        );
      }

      const pieData = raw
        .map((item: any) => {
          const count = Number(item?.[valueKey]) || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return {
            label: String(item?.label ?? ""),
            count,
            percentage,
          };
        })
        .filter((d: any) => d.label);

      return (
        <PieChart
          key={hospitalId}
          data={pieData}
          title={t(title)}
          tooltipTitle={t(tooltipTitle)}
          labelSize={props?.categoryFontSize ?? 13}
          showLegend={props?.showLegend ?? true}
        />
      );
    }

    if (isLoading) {
      return <BarGraphLoader {...props} />;
    }

    return (
      <BarChart
        key={hospitalId}
        title={t(title)}
        tooltipTitle={t(tooltipTitle)}
        data={data}
        {...props}
      />
    );
  };

  return (
    <Box ref={screenshotRef} flex={1} py={2} sx={{ px: isExporting ? 2 : 0 }}>
      <Grid flex={1} container spacing={1.5}>
        {allOverviewMetricsData?.slice(0, 6)?.map((item) => (
          <Grid key={item.label} display="grid" size={{ xs: 6, md: 2 }}>
            {renderMetricCard(item)}
          </Grid>
        ))}

        <Grid display="grid" size={{ xs: 6, md: 2 }}>
          <Stack flex={1} gap={1.5}>
            {allOverviewMetricsData?.slice(6).map(renderMetricCard)}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingCaseBreakdown}
            data={caseDelayBreakdown ?? []}
            title="Case_Delay_Breakdown"
            tooltipTitle="Case_Delay_Breakdown_Tooltip"
            props={{
              valueKey: "count",
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <ChartContainer
            isLoading={isGettingOtTIme}
            data={otTimeData ?? []}
            title="OT_Time_Breakdown"
            tooltipTitle="OT_Time_Breakdown_Tooltip"
            chartType={CHART_TYPE.PIE}
            props={{
              showLabels: false,
              showXAxis: false,
              categoryFontSize: 12,
              labelColNumber: 2,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingCaseInterval}
            data={caseIntervalBarData}
            title="Case_Interval_Duration_Avg"
            tooltipTitle="Case_Interval_Duration_Avg_Tooltip"
            props={{
              showLabels: false,
              showYAxis: false,
              direction: BAR_GRAPH_DIRECTION.HORIZONTAL,
              categoryFontSize: 12,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isGettingVolumeSpeciality}
            data={transformedCaseVolumeSpeciality}
            title="Case_Volume_by_Procedure"
            tooltipTitle="Case_Volume_by_Procedure_Tooltip"
            props={{
              showLabels: false,
              showXAxis: false,
              categoryFontSize: 12,
              categoryPosition: "TOP",
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <ChartContainer
            isLoading={isLoadingDelayedStartSeverityFrequency}
            data={delayedStartSeverityFrequencyData?.data}
            title={"Delayed Start – Frequency vs Severity"}
            tooltipTitle={"Delayed Start – Frequency vs Severity"}
            chartType={CHART_TYPE.SCATTER}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <ChartContainer
            isLoading={isLoadingDelayedEndSeverityFrequency}
            data={delayedEndSeverityFrequencyData?.data}
            title={"Delayed End – Frequency vs Severity"}
            tooltipTitle={"Delayed End – Frequency vs Severity"}
            chartType={CHART_TYPE.SCATTER}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartContainer
            isLoading={isGettingCasesMovedPerOt}
            data={casesMovedPerOtData}
            title="Cases_Moved_Per_OT"
            tooltipTitle="Cases_Moved_Per_OT_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              categoryPosition: "TOP",
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Overview;
